import { LitElement, html, unsafeCSS, TemplateResult, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { teaReport } from '$tea';
import { readUserConfig, UserConfig } from '../utils/config';
import { closeIconSvg, verticalbarIconSvg, RTL_LANGUAGE } from '../utils/constant';
import { CookieConsent } from '../interface/cookie-banner';
import cookieConfigManager from '../utils/cookie-consent-manager';
import styles from './setting-modal.scss';
import globalStyles from '../styles/global.module.scss';
import { initConsentSDK } from '../utils/consent';
import './switch';

const touchEventNames = ['touchstart', 'touchmove', 'touchend', 'scroll'];

@customElement('cookie-setting-modal')
export class SettingsModal extends LitElement {
  static override styles = unsafeCSS(styles);

  @property({
    attribute: 'z-index',
  })
  zIndex = 10002;
  @property({
    converter: (value: string | null) => 'true' === value,
  })
  visible = false;
  @property({
    attribute: 'user-config-ele-id',
  })
  userConfigEleId!: string;
  @property() locale!: string;

  @property({
    attribute: 'setting-modal-close-disabled',
  })
  settingModalCloseDisabled = false;

  @state() deferredVisible = false;
  @state() userConfig?: UserConfig;
  @state() cookieConsent: CookieConsent = {};
  @state() customTheme = '';
  @state() tenantId = '';

  private modalBackdropRef: Ref<HTMLDivElement> = createRef();
  private scrollableListRef: Ref<HTMLDivElement> = createRef();

  override connectedCallback(): void {
    super.connectedCallback();

    this.initUserConfig();
    if (this.userConfig) {
      this.cookieConsent = cookieConfigManager.getCookieConsents(this.userConfig);
      const { consent, region, consentApiDomain, idcRegion, disableTea, tea, tenantId } = this.userConfig;
      initConsentSDK({ config: consent, region, consentApiDomain, idcRegion, disableTea, tea, tenantId });
    }

    if (this.visible) {
      requestAnimationFrame(() => (this.deferredVisible = true));
    }
  }

  override willUpdate(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('userConfigEleId') || changedProperties.has('locale')) {
      this.initUserConfig();
    }
    if (changedProperties.has('visible')) {
      if (this.visible) {
        this.cookieConsent = this.userConfig ? cookieConfigManager.getCookieConsents(this.userConfig) : {};
        requestAnimationFrame(() => (this.deferredVisible = true));
        this.lockScroll();

        teaReport('cookie_setting_show');
        this.dispatchEvent(
          new CustomEvent('customBannerEvents', {
            detail: {
              eventName: 'onSettingShow',
            },
            bubbles: true,
            composed: true,
          }),
        );
      } else {
        const backdropEle = this.modalBackdropRef.value;
        const transitionEnd = () => {
          backdropEle?.removeEventListener('transitionend', transitionEnd);
          this.deferredVisible = false;
        };
        backdropEle?.addEventListener('transitionend', transitionEnd);
        this.unlockScroll();
        for (const eventName of touchEventNames) {
          this.scrollableListRef.value?.removeEventListener(eventName, this.stopTouchEventPropagation);
        }
      }
    }
  }

  override updated(changedPropertes: Map<string, unknown>) {
    if (changedPropertes.has('visible')) {
      if (this.visible && this.scrollableListRef.value) {
        for (const eventName of touchEventNames) {
          this.scrollableListRef.value.addEventListener(eventName, this.stopTouchEventPropagation);
        }
      }
    }
  }

  private stopTouchEventPropagation(event: Event): void {
    event.stopPropagation();
  }

  private lockScroll(): void {
    document.body.classList.add(globalStyles.disableScroll);
  }

  private unlockScroll(): void {
    document.body.classList.remove(globalStyles.disableScroll);
  }

  private initUserConfig(): void {
    const userConfig = readUserConfig(this.userConfigEleId, this.locale);
    if (userConfig) {
      this.userConfig = userConfig;
      this.customTheme = userConfig.customTheme;
      this.tenantId = userConfig.tenantId;
    }
  }

  private handleClose(): void {
    teaReport('cookie_setting_close');
    this.dispatchEvent(new Event('close'));
  }

  private disagreeToAll({ value, groupName }: { value: boolean; groupName: string }): void {
    const consent = cookieConfigManager.getCategoryConsents({ groupName, value });
    this.cookieConsent = {
      ...(this.cookieConsent || {}),
      ...consent,
    };
    teaReport('cookie_setting_toggle', {
      state: value ? 1 : 0,
      type: 'category',
      name: groupName,
    });
  }

  private disagreeToOne(code: string): void {
    const originValue = this.cookieConsent[code];
    this.cookieConsent = {
      ...this.cookieConsent,
      [code]: !originValue,
    };
    teaReport('cookie_setting_toggle', {
      state: originValue ? 0 : 1,
      type: 'vendor',
      name: code,
    });
  }

  private handleSave(): void {
    cookieConfigManager.setCookieConsents(this.cookieConsent);
    this.dispatchEvent(new Event('close'));
    this.dispatchEvent(
      new CustomEvent('save', {
        detail: this.cookieConsent,
      }),
    );
    teaReport('cookie_setting_save', {
      result: Object.keys(this.cookieConsent)
        .filter((code) => this.cookieConsent[code])
        .join(','),
    });

    const isAcceptAll = Object.keys(this.cookieConsent).every((code) => this.cookieConsent[code]);
    const isRejectAll = Object.keys(this.cookieConsent).every((code) => !this.cookieConsent[code]);
    if (isAcceptAll) {
      teaReport('cookie_banner_accept');
    } else if (isRejectAll) {
      teaReport('cookie_banner_decline');
    }
  }

  private renderVendorDescription(description: string): (TemplateResult | typeof nothing)[] {
    const descriptionArray: {
      type: string;
      value: string;
      ref?: string;
    }[] = [];
    let patt = new RegExp('\\[([^\\]]+)\\]\\(([^)]+)\\)', 'g');
    let result: RegExpExecArray | null;
    // eslint-disable-next-line no-cond-assign
    while ((result = patt.exec(description)) !== null) {
      const preDescriptionText = description.slice(0, result.index);
      if (preDescriptionText) {
        descriptionArray.push({
          type: 'normalText',
          value: preDescriptionText,
        });
      }
      descriptionArray.push({
        type: 'linkText',
        value: result[1],
        ref: result[2],
      });
      description = description.slice(result.index + result[0].length);
    }
    if (description) {
      descriptionArray.push({
        type: 'normalText',
        value: description,
      });
    }
    return descriptionArray.map((block) => {
      const { type, value, ref } = block;
      switch (type) {
        case 'normalText':
          return html`<span>${value}</span>`;
        case 'linkText':
          return html`
            <a
              class="tiktok-cookie-vendor-context-ref"
              href=${ifDefined(ref)}
              target="_blank"
              rel="noopener noreferrer"
            >
              ${value}
            </a>
          `;
        default:
          return nothing;
      }
    });
  }

  override render() {
    const { locale = 'en', tenantId } = this;
    const {
      cookieSettingsTitle = '',
      cookieSettingSaveBtn = '',
      cookieSettingsSubTitle = '',
      cookieSettingsFooter = '',
    } = this.userConfig?.i18n ?? {};
    if (!this.deferredVisible && !this.visible) {
      return nothing;
    }

    const settings = cookieConfigManager.getSettings();
    const agreeToAll = (keys: string[]): boolean => {
      const consent = keys.reduce((o, key) => ({ ...o, [key]: this.cookieConsent[key] }), {});
      return Object.values(consent).every((value) => Boolean(value));
    };

    return html`
      <style>
        :host {
          ${this.customTheme}
        }
      </style>
      <div
        class=${classMap({
          'tiktok-cookie-setting-portal': true,
          'is-rtl': RTL_LANGUAGE.includes(locale),
          ...(tenantId ? { [tenantId]: true } : {}),
        })}
        style=${styleMap({
          zIndex: `${this.zIndex}`,
        })}
      >
        <div
          ${ref(this.modalBackdropRef)}
          class=${classMap({
            'tiktok-cookie-setting-backdrop': true,
            visible: this.visible && this.deferredVisible,
          })}
        ></div>
        <div
          class=${classMap({
            'tiktok-cookie-setting-modal-container': true,
            visible: this.visible && this.deferredVisible,
          })}
        >
          <section class="tiktok-cookie-setting-modal">
            <div class="tiktok-cookie-setting-header-container">
              ${cookieSettingsTitle}
              ${this.settingModalCloseDisabled
                ? nothing
                : html`
                    <div
                      class="tiktok-cookie-setting-modal-close"
                      @click=${this.handleClose}
                      .innerHTML=${closeIconSvg}
                    ></div>
                  `}
            </div>
            <div class="tiktok-cookie-setting-modal-description-container">
              <div class="tiktok-cookie-setting-modal-description">
                <p class="tiktok-cookie-manage-tip-context">${this.renderVendorDescription(cookieSettingsSubTitle)}</p>
              </div>
            </div>

            <div ${ref(this.scrollableListRef)} class="tiktok-cookie-setting-content">
              ${settings.map(
                (setting) => html`
                  <div class="tiktok-cookie-vendor-group show-detail">
                    <h1 class="tiktok-cookie-vendor-group-name">
                      <div class="tiktok-cookie-vendor-group-name-outside with-hover-effect">${setting.groupName}</div>
                      <span class="tiktok-cookie-vertical-info-icon" .innerHTML=${verticalbarIconSvg}></span>
                    </h1>
                    <cookie-setting-switch
                      .value=${setting.disabled
                        ? true
                        : agreeToAll(cookieConfigManager.getSettingItemKeys(setting.items, setting.groupEntityKeys))}
                      .disabled=${setting.disabled}
                      @checkedChange=${(e: CustomEvent<boolean>) => {
                        if (!setting.disabled) {
                          this.disagreeToAll({ value: e.detail, groupName: setting.groupName });
                        }
                      }}
                    ></cookie-setting-switch>
                  </div>

                  ${html`<div class="tiktok-cookie-setting-desc">${setting.groupDescription}</div>`}
                  ${cookieConfigManager.getSettingItemKeys(setting.items).map(
                    (key: string) => html`
                      <div class="tiktok-cookie-vendor-container">
                        <div class="tiktok-cookie-vendor">
                          <div class="tiktok-cookie-vendor-label-outside with-hover-effect">
                            <p class="tiktok-cookie-vendor-label">${setting.items[key]?.vendorName || ''}</p>
                          </div>
                          <span class="tiktok-cookie-vertical-info-icon" .innerHTML=${verticalbarIconSvg}></span>
                          <cookie-setting-switch
                            .value=${Boolean(this.cookieConsent[key])}
                            .disabled=${setting.disabled}
                            @checkedChange=${() => {
                              if (!setting.disabled) {
                                this.disagreeToOne(key);
                              }
                            }}
                          >
                          </cookie-setting-switch>
                        </div>
                        ${html`
                          <p class="tiktok-cookie-vendor-context">
                            ${this.renderVendorDescription(setting.items[key]?.description)}
                          </p>
                        `}
                      </div>
                    `,
                  )}
                `,
              )}
            </div>

            <div class="tiktok-cookie-setting-footer">
              <p class="tiktok-cookie-setting-footer-desc" .innerHTML=${cookieSettingsFooter}></p>
              <button class="tiktok-cookie-setting-footer-btn-primary" @click=${this.handleSave}>
                ${cookieSettingSaveBtn}
              </button>
            </div>
          </section>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cookie-setting-modal': SettingsModal;
  }
}
